import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Button } from './Button';
import { Spinner } from './Spinner';

export interface Column<T> {
  key: string;
  label: string;
  sortable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
  render?: (item: T, index: number) => React.ReactNode;
}

export interface PaginationConfig {
  // Standard format
  page?: number;
  pageSize?: number;
  total?: number;
  // Alternative format (used in some pages)
  currentPage?: number;
  totalPages?: number;
  totalItems?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  keyExtractor: (item: T) => string;
  pagination?: PaginationConfig;
  isLoading?: boolean;
  emptyMessage?: string;
  onRowClick?: (item: T) => void;
  selectedRows?: string[];
  onSelectionChange?: (keys: string[]) => void;
  stickyHeader?: boolean;
}

export function DataTable<T>({
  data,
  columns,
  keyExtractor,
  pagination,
  isLoading = false,
  emptyMessage = 'Nenhum registro encontrado',
  onRowClick,
  stickyHeader = false,
}: DataTableProps<T>) {
  // Normalize pagination to handle both formats
  const currentPage = pagination?.currentPage ?? pagination?.page ?? 1;
  const pageSize = pagination?.pageSize ?? 20;
  const total = pagination?.totalItems ?? pagination?.total ?? data.length;
  const totalPages = pagination?.totalPages ?? (pagination ? Math.ceil(total / pageSize) : 1);

  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-secondary-700">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-secondary-700">
          <thead className={`bg-gray-50 dark:bg-secondary-800 ${stickyHeader ? 'sticky top-0 z-10' : ''}`}>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`
                    px-4 py-3 text-xs font-semibold uppercase tracking-wider
                    text-gray-500 dark:text-gray-400
                    ${alignClasses[column.align || 'left']}
                    ${column.width || ''}
                  `}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-secondary-900 divide-y divide-gray-200 dark:divide-secondary-700">
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-12 text-center text-gray-500 dark:text-gray-400"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((item, index) => (
                <tr
                  key={keyExtractor(item)}
                  onClick={() => onRowClick?.(item)}
                  className={`
                    ${onRowClick ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-secondary-800' : ''}
                    transition-colors duration-150
                  `}
                >
                  {columns.map((column) => (
                    <td
                      key={`${keyExtractor(item)}-${column.key}`}
                      className={`
                        px-4 py-3 text-sm text-gray-900 dark:text-gray-100
                        ${alignClasses[column.align || 'left']}
                        ${column.width || ''}
                      `}
                    >
                      {column.render
                        ? column.render(item, index)
                        : (item as Record<string, unknown>)[column.key] as React.ReactNode}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && total > 0 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-secondary-700 bg-white dark:bg-secondary-900 rounded-b-lg">
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <span>
              Mostrando {((currentPage - 1) * pageSize) + 1} -{' '}
              {Math.min(currentPage * pageSize, total)} de{' '}
              {total}
            </span>
            {pagination.onPageSizeChange && pagination.pageSizeOptions && (
              <select
                value={pageSize}
                onChange={(e) => pagination.onPageSizeChange?.(Number(e.target.value))}
                className="ml-2 px-2 py-1 text-sm border border-gray-300 dark:border-secondary-600 rounded bg-white dark:bg-secondary-800"
              >
                {pagination.pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size} por pagina
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => pagination.onPageChange(1)}
              disabled={currentPage === 1}
              aria-label="Primeira pagina"
            >
              <ChevronsLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => pagination.onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              aria-label="Pagina anterior"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>

            <span className="px-3 py-1 text-sm text-gray-700 dark:text-gray-300">
              {currentPage} / {totalPages}
            </span>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => pagination.onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              aria-label="Proxima pagina"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => pagination.onPageChange(totalPages)}
              disabled={currentPage >= totalPages}
              aria-label="Ultima pagina"
            >
              <ChevronsRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataTable;
